import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { Product } from '@shared/components/ui/Product';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faShoppingCart, faHome, faCheckCircle } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useToast } from '@shared/hooks/use-toast';
import { StockErrorApiAdapter } from '../../../adapters/StockErrorApiAdapter';
import { translateOrderApiError } from '../../../../CreateEditOrders/utils/errorTranslator';
import type { OrderLine, OrderProduct } from '../../../models/Order';

interface StockCheckItem {
  key: string;
  productId: number;
  product: OrderProduct;
  requiredQty: number;
}

const expandOrderLinesForStockCheck = (lines: OrderLine[]): StockCheckItem[] => {
  const aggregated = new Map<number, StockCheckItem>();

  lines.forEach((line) => {
    if (!line) return;

    if (line.pack_id && line.pack) {
      (line.pack.lines ?? []).forEach((packLine) => {
        if (!packLine?.product_id || !packLine.product) return;

        const requiredQty = (packLine.qty || 1) * (line.qty || 1);
        const existing = aggregated.get(packLine.product_id);

        if (existing) {
          existing.requiredQty += requiredQty;
          return;
        }

        aggregated.set(packLine.product_id, {
          key: `pack-${line.id}-${packLine.product_id}`,
          productId: packLine.product_id,
          product: packLine.product,
          requiredQty,
        });
      });
      return;
    }

    if (line.product_id && line.product) {
      const existing = aggregated.get(line.product_id);

      if (existing) {
        existing.requiredQty += line.qty;
        return;
      }

      aggregated.set(line.product_id, {
        key: `product-${line.id}`,
        productId: line.product_id,
        product: line.product,
        requiredQty: line.qty,
      });
    }
  });

  return Array.from(aggregated.values());
};

const getStockDisponible = (productStocks: Record<number, ProductStock>, productId: number) =>
  productStocks[productId]?.stock?.disponible ?? 0;

const hasStockIssue = (item: StockCheckItem, productStocks: Record<number, ProductStock>) =>
  item.requiredQty > getStockDisponible(productStocks, item.productId);

interface ProductStock {
  id: number;
  name: string;
  code: string;
  stock: {
    disponible: number;
    reservado: number;
    entrante: number;
  };
}

interface OrderData {
  id: number;
  lines: OrderLine[];
}

const getStockCheckItems = (order: OrderData | null) =>
  order?.lines ? expandOrderLinesForStockCheck(order.lines) : [];

export interface StockErrorModalRef {
  openModal: (orderData: OrderData) => void;
  closeModal: () => void;
}

interface StockErrorModalProps {
  onSuccess?: () => void;
}

export const StockErrorModal = forwardRef<StockErrorModalRef, StockErrorModalProps>(({ onSuccess }, ref) => {
  const { t } = useTranslation('viewOrdersModule');
  const { toast } = useToast();
  const modalRef = useRef<ModalCenterRef>(null);
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [productStocks, setProductStocks] = useState<Record<number, ProductStock>>({});
  const [isLoadingStock, setIsLoadingStock] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Función para obtener el stock de los productos
  const fetchProductStocks = async (productIds: number[]) => {
    if (productIds.length === 0) {
      setHasError(false);
      return;
    }

    setIsLoadingStock(true);
    setHasError(false);

    try {
      const httpClient = createHttpClient(apiClientWH);
      const productIdsParam = productIds.join(',');
      const raw = await httpClient.get(`/products?product_ids=${productIdsParam}`);
      // Mismo cuerpo que devuelve la API: { data: Product[], meta, links }. HttpClient ya devuelve response.data de axios.
      const products = Array.isArray(raw) ? raw : ((raw as { data?: unknown })?.data ?? []);

      // Convertir array a objeto indexado por ID para fácil acceso
      const stocksMap: Record<number, ProductStock> = {};
      products.forEach((product: any) => {
        if (product && product.id) {
          stocksMap[product.id] = {
            id: product.id,
            name: product.name || 'Producto sin nombre',
            code: product.code || 'Sin código',
            stock: product.stock || {
              disponible: 0,
              reservado: 0,
              entrante: 0,
            },
          };
        }
      });

      setProductStocks(stocksMap);
    } catch (error: any) {
      console.error('Error fetching product stocks:', error);
      setHasError(true);

      // Mostrar toast con el error
      toast({
        title: t('stockError.fetchError.title'),
        description: error?.message || t('stockError.fetchError.description'),
        variant: 'destructive',
      });
    } finally {
      setIsLoadingStock(false);
    }
  };

  useImperativeHandle(ref, () => ({
    openModal: (order: OrderData) => {
      // Validar que order y order.lines existan
      if (!order || !order.lines || !Array.isArray(order.lines)) {
        console.error('Invalid order data provided to StockErrorModal');
        toast({
          title: t('stockError.invalidData.title'),
          description: t('stockError.invalidData.description'),
          variant: 'destructive',
        });
        return;
      }

      setOrderData(order);
      setHasError(false);

      const productIds = expandOrderLinesForStockCheck(order.lines).map((item) => item.productId);

      fetchProductStocks(productIds);
      modalRef.current?.openModal();
    },
    closeModal: () => modalRef.current?.closeModal(),
  }));

  const handleClose = () => {
    modalRef.current?.closeModal();
  };

  const handleBack = () => {
    modalRef.current?.closeModal();
  };

  const handleAccept = async () => {
    if (!orderData) return;

    setIsSubmitting(true);

    try {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new StockErrorApiAdapter(httpClient);

      const response = await adapter.unassignStock(orderData.id);

      if (response.success) {
        toast({
          title: t('stockError.success.title'),
          description: response.message,
          variant: 'default',
        });
        onSuccess?.();
      } else {
        toast({
          title: t('stockError.error.title'),
          description: response.message,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      // Traducir el error usando el sistema de traducción
      const translatedError = translateOrderApiError(error, t);

      toast({
        title: t('stockError.error.title'),
        description: translatedError,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const stockCheckItems = getStockCheckItems(orderData);
  const productsWithStockIssues = stockCheckItems.filter((item) => hasStockIssue(item, productStocks));

  return (
    <ModalCenter
      ref={modalRef}
      title={t('stockError.title')}
      icon={
        <button
          onClick={handleBack}
          className="flex items-center justify-center w-6 h-6 hover:bg-hover-press rounded transition-colors"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
        </button>
      }
      type="default"
      fullScreenMobile={true}
      width="lg"
      className="max-h-[90vh]"
    >
      <div className="flex flex-col h-full">
        {/* Contenido principal */}
        <div className="flex-1 space-y-4 mt-2 pb-24 md:pb-0 max-h-[70vh] overflow-y-auto">
          <p className="text-sm">{t('stockError.description')}</p>

          {/* Verificar si hay errores */}
          {hasError && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-error/10 flex items-center justify-center mb-4">
                <FontAwesomeIcon icon={faCheckCircle} className="w-8 h-8 text-error" />
              </div>
              <h3 className="text-lg font-medium text-title mb-2">{t('stockError.errorState.title')}</h3>
              <p className="text-sm text-complementary-text max-w-md">{t('stockError.errorState.description')}</p>
            </div>
          )}

          {/* Verificar si hay productos con problemas de stock */}
          {!hasError && !isLoadingStock && productsWithStockIssues.length === 0 && stockCheckItems.length > 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mb-4">
                <FontAwesomeIcon icon={faCheckCircle} className="w-8 h-8 text-success" />
              </div>
              <p className="text-sm text-complementary-text max-w-md">{t('stockError.noStockIssues.description')}</p>
            </div>
          )}

          {/* Tabla de productos - Desktop - Solo mostrar si hay productos con problemas */}
          {!hasError && productsWithStockIssues.length > 0 && (
            <div className="pt-4">
              <div className="space-y-4">
                {/* Headers de la tabla - Solo visible en desktop */}
                <div className="hidden md:grid grid-cols-12 gap-4 text-xs font-medium text-complementary-text border-b border-card-border pb-3">
                  <div className="col-span-6">{t('stockError.product')}</div>
                  <div className="col-span-3 text-center">{t('stockError.orderQuantity')}</div>
                  <div className="col-span-3 text-center">{t('stockError.systemQuantity')}</div>
                </div>

                {/* Filas de productos - Desktop */}
                {productsWithStockIssues.map((item) => {
                  const stockDisponible = getStockDisponible(productStocks, item.productId);

                  return (
                    <div
                      key={item.key}
                      className="hidden md:grid grid-cols-12 gap-4 items-center border-b border-card-border last:border-0 !mt-0 py-3"
                    >
                      <div className="col-span-6 flex items-center gap-3">
                        <Product
                          id={item.product.id}
                          name={item.product.name}
                          code={item.product.code}
                          src={item.product.image_hash}
                          acronym={item.product.name
                            .split(' ')
                            .map((word) => word[0])
                            .join('')
                            .toUpperCase()}
                          className="flex-1"
                          enableLineClamp={false}
                          showTooltip={true}
                        />
                      </div>

                      <div className="col-span-3 text-sm text-center">{item.requiredQty}</div>

                      <div className="col-span-3 text-sm text-center text-error">
                        {isLoadingStock ? '...' : stockDisponible}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Cards de productos - Mobile - Solo mostrar si hay productos con problemas */}
          {!hasError && productsWithStockIssues.length > 0 && (
            <div className="md:hidden space-y-4">
              {productsWithStockIssues.map((item) => {
                const stockDisponible = getStockDisponible(productStocks, item.productId);

                return (
                  <div key={item.key} className="space-y-3">
                    <Product
                      id={item.product.id}
                      name={item.product.name}
                      code={item.product.code}
                      src={item.product.image_hash}
                      acronym={item.product.name
                        .split(' ')
                        .map((word) => word[0])
                        .join('')
                        .toUpperCase()}
                      className="flex-1"
                      enableLineClamp={false}
                      showTooltip={true}
                    />

                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faShoppingCart} className="w-4 h-4 text-primary" />
                        <span className="font-medium">{item.requiredQty}</span>
                        <span className="text-complementary-text">{t('stockError.inOrder')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faHome} className="w-4 h-4 text-error" />
                        <span className="font-medium text-error">{isLoadingStock ? '...' : stockDisponible}</span>
                        <span className="text-complementary-text">{t('stockError.inSystem')}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Botones de acción - Fijos en mobile */}
        <div className="fixed bottom-0 left-0 right-0 p-4 md:pb-0 md:px-0 md:relative md:border-t-0">
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleClose} size="lg">
              {t('stockError.cancel')}
            </Button>
            <Button variant="primary" onClick={handleAccept} size="lg" disabled={isSubmitting}>
              {isSubmitting ? t('stockError.submitting') : t('stockError.accept')}
            </Button>
          </div>
        </div>
      </div>
    </ModalCenter>
  );
});
